/**
 * Mobile Category Tiles — assets/mobile-category-tiles.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Custom element <mobile-category-tiles>.
 *
 * Behaviour
 *  Renders a horizontally scrollable strip of circular category tiles.
 *  Prev/next arrow buttons scroll the track by one tile width + gap.
 *  Arrows show/hide automatically based on the current scroll position:
 *    - Prev hidden when at the leftmost position (scrollLeft === 0)
 *    - Next hidden when at the rightmost position (fully scrolled)
 *  A ResizeObserver (with a window-resize fallback) recalculates the scroll
 *  step whenever the track resizes due to orientation change or viewport resize.
 *
 * Scroll step
 *  Calculated as: first tile offsetWidth + computed gap between tiles.
 *  Tile width is set by CSS (tile_size Liquid variable) so it is stable from
 *  the moment connectedCallback fires. The ResizeObserver keeps it accurate
 *  if the track layout ever changes.
 *
 * No third-party dependencies — plain ES6.
 * ─────────────────────────────────────────────────────────────────────────────
 */

(function () {
  'use strict';

  if (customElements.get('mobile-category-tiles')) return;

  const MobileCategoryTiles = class extends HTMLElement {

    /* ── Lifecycle ──────────────────────────────────────────────────────── */

    connectedCallback() {
      // DOM refs
      this.track     = this.querySelector('.mct__track');
      this.btnPrev   = this.querySelector('.mct__btn-prev');
      this.btnNext   = this.querySelector('.mct__btn-next');
      this.sectionEl = this.closest('[id^="shopify-section-"]');

      // Config from Liquid data attributes
      this.showArrows = this.dataset.showArrows !== 'false';

      // State
      this._scrollStep     = 0;
      this._resizeObserver = null;
      this._onPageScroll   = null;

      // Homepage: match transparent header scroll behaviour
      if (document.body.classList.contains('template-index')) {
        this._onPageScroll = () => {
          const scrolled = window.scrollY > 10;
          this.classList.toggle('mct--scrolled', scrolled);
        };
        window.addEventListener('scroll', this._onPageScroll, { passive: true });

        // Override --transparent-header-text-color on .section-header directly.
        // CSS child overrides don't work because the variable is set on a wrapping
        // parent element. Direct JS override is the only reliable fix.
        // Scoped to mobile only (<1024px) — the section is CSS-hidden on desktop
        // but the JS would still fire without this guard, breaking the white
        // transparent-header text on desktop.
        const sectionHeader = document.querySelector('.section-header');
        if (sectionHeader) {
          this._sectionHeader = sectionHeader;
          const textColor = `rgb(${getComputedStyle(document.documentElement).getPropertyValue('--text-color')})`;
          this._headerColorMq = window.matchMedia('(max-width: 1023px)');
          this._applyHeaderColor = () => {
            if (this._headerColorMq.matches) {
              this._sectionHeader.style.setProperty('--transparent-header-text-color', textColor);
            } else {
              this._sectionHeader.style.removeProperty('--transparent-header-text-color');
            }
          };
          this._applyHeaderColor();
          this._headerColorMq.addEventListener('change', this._applyHeaderColor);
        }
      }

      // Guard — no track means no blocks were configured
      if (!this.track) return;

      this._calcScrollStep();
      this._updateArrows();
      this._bindEvents();
    }

    disconnectedCallback() {
      // Clean up ResizeObserver
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
      }

      // Clean up scroll listener
      if (this.track && this._onScroll) {
        this.track.removeEventListener('scroll', this._onScroll);
      }

      // Clean up arrow click listeners
      if (this.btnPrev && this._onPrevClick) {
        this.btnPrev.removeEventListener('click', this._onPrevClick);
      }
      if (this.btnNext && this._onNextClick) {
        this.btnNext.removeEventListener('click', this._onNextClick);
      }

      // Clean up window resize fallback (only present when ResizeObserver unsupported)
      if (this._onResize) {
        window.removeEventListener('resize', this._onResize);
        window.removeEventListener('on:debounced-resize', this._onResize);
      }

      // Clean up homepage page-scroll listener
      if (this._onPageScroll) {
        window.removeEventListener('scroll', this._onPageScroll);
      }

      // Restore --transparent-header-text-color on disconnect
      if (this._headerColorMq && this._applyHeaderColor) {
        this._headerColorMq.removeEventListener('change', this._applyHeaderColor);
      }
      const sectionHeader = this._sectionHeader || document.querySelector('.section-header');
      if (sectionHeader) {
        sectionHeader.style.removeProperty('--transparent-header-text-color');
      }
    }

    /* ── Scroll step calculation ────────────────────────────────────────── */

    /**
     * Measures one tile's width plus the gap between tiles.
     * Arrow clicks call scrollBy() with this value so exactly one tile
     * scrolls into view per click.
     * Safe to call before images load — tile width is set by CSS, not content.
     */
    _calcScrollStep() {
      const firstTile = this.track.querySelector('.mct__tile');
      if (!firstTile) {
        this._scrollStep = 0;
        return;
      }
      const gap = parseFloat(window.getComputedStyle(this.track).gap) || 8;
      this._scrollStep = firstTile.offsetWidth + gap;
    }

    /* ── Arrow visibility ───────────────────────────────────────────────── */

    /**
     * Shows/hides prev and next arrows based on the current scroll position.
     * Called on initial mount, on every scroll event, and on resize.
     *
     * Uses aria-hidden to keep hidden arrows invisible to assistive technology.
     * The -1 tolerance on atEnd handles sub-pixel rounding in some browsers.
     */
    _updateArrows() {
      if (!this.showArrows || (!this.btnPrev && !this.btnNext)) return;

      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const atStart = scrollLeft <= 0;
      const atEnd   = scrollLeft + clientWidth >= scrollWidth - 1;

      if (this.btnPrev) {
        this.btnPrev.classList.toggle('mct--visible', !atStart);
        this.btnPrev.setAttribute('aria-hidden', String(atStart));
      }

      if (this.btnNext) {
        this.btnNext.classList.toggle('mct--visible', !atEnd);
        this.btnNext.setAttribute('aria-hidden', String(atEnd));
      }
    }

    /* ── Event bindings ─────────────────────────────────────────────────── */

    _bindEvents() {
      // Scroll event — update arrow visibility as user swipes
      this._onScroll = () => this._updateArrows();
      this.track.addEventListener('scroll', this._onScroll, { passive: true });

      // Prev arrow click — scroll one step left
      if (this.btnPrev) {
        this._onPrevClick = () => {
          this.track.scrollBy({ left: -this._scrollStep, behavior: 'smooth' });
        };
        this.btnPrev.addEventListener('click', this._onPrevClick);
      }

      // Next arrow click — scroll one step right
      if (this.btnNext) {
        this._onNextClick = () => {
          this.track.scrollBy({ left: this._scrollStep, behavior: 'smooth' });
        };
        this.btnNext.addEventListener('click', this._onNextClick);
      }

      // ResizeObserver: recalculate scroll step and arrow state when the
      // track resizes (orientation change, viewport resize, etc.).
      // Observing the track directly is more reliable than window resize
      // because it fires immediately when the element's dimensions change.
      if (typeof ResizeObserver !== 'undefined') {
        this._resizeObserver = new ResizeObserver(() => {
          this._calcScrollStep();
          this._updateArrows();
        });
        this._resizeObserver.observe(this.track);
      } else {
        // Fallback for older iOS Safari — matches the sticky-search-bar pattern
        this._onResize = () => {
          this._calcScrollStep();
          this._updateArrows();
        };
        window.addEventListener('resize', this._onResize, { passive: true });
        window.addEventListener('on:debounced-resize', this._onResize);
      }
    }

  };

  customElements.define('mobile-category-tiles', MobileCategoryTiles);

})();
